import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  createBusinessUnit,
  fetchBusinessDomains,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function BusinessUnitCreatePage() {
  const { businessDomainId: domainIdFromParam } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [domains, setDomains] = useState([]);
  const [form, setForm] = useState({
    businessDomainId: domainIdFromParam || "",
    businessDomainName: "",
    businessUnitName: "",
    vision: "",
    startDate: "",
    endDate: "",
    url: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchBusinessDomains(COMPANY_ID);
        const list = Array.isArray(data) ? data : [];
        setDomains(list);
        // If domain is pre-selected from param, populate the name
        if (domainIdFromParam) {
          const found = list.find((d) => d.businessDomainId === domainIdFromParam);
          if (found) {
            setForm((prev) => ({
              ...prev,
              businessDomainId: found.businessDomainId,
              businessDomainName: found.businessDomainName,
            }));
          }
        }
      } catch (e) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainIdFromParam]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleDomainChange = (e) => {
    const selected = domains.find((d) => d.businessDomainId === e.target.value);
    setForm({
      ...form,
      businessDomainId: e.target.value,
      businessDomainName: selected ? selected.businessDomainName : "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createBusinessUnit({
        companyId: COMPANY_ID,
        businessDomainId: form.businessDomainId,
        businessDomainName: form.businessDomainName,
        businessUnitName: form.businessUnitName,
        vision: form.vision,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        url: form.url || null,
      });
      notify.success(t("message.create.success"));
      navigate(`/strategy/${form.businessDomainId}/business-units`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("businessUnit.create.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* 事業ドメイン */}
          <Grid size={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>{t("businessDomain.title")}</InputLabel>
              <Select
                name="businessDomainId"
                value={form.businessDomainId}
                onChange={handleDomainChange}
                label={t("businessDomain.title")}
                disabled={!!domainIdFromParam}
              >
                {domains.map((d) => (
                  <MenuItem key={d.businessDomainId} value={d.businessDomainId}>
                    {d.businessDomainName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* 事業単位名 */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth required
              label={t("businessUnit.name")}
              name="businessUnitName"
              value={form.businessUnitName}
              onChange={handleChange}
            />
          </Grid>

          {/* ビジョン */}
          <Grid size={12}>
            <TextField
              fullWidth multiline rows={2}
              label={t("businessUnit.vision")}
              name="vision"
              value={form.vision}
              onChange={handleChange}
            />
          </Grid>

          {/* 開始日 */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={t("businessUnit.startDate")}
              name="startDate"
              value={form.startDate}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {/* 終了日 */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={t("businessUnit.endDate")}
              name="endDate"
              value={form.endDate}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>

          {/* URL */}
          <Grid size={12} md={6}>
            <TextField
              fullWidth
              label={t("businessUnit.url")}
              name="url"
              value={form.url}
              onChange={handleChange}
            />
          </Grid>

        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.add")}</Button>
          <Button variant="outlined" onClick={() => navigate(domainIdFromParam ? `/strategy/${domainIdFromParam}/business-units` : `/strategy`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
